/**
 * usePlanNorthRotation — computes CSS transforms that rotate the plan towards
 * geographic north and scale it to remain within its container.
 *
 * Returns two style objects to apply to two different DOM nodes:
 *  - wrapperStyle  → the outer wrapper div (rotates the whole renderer including geographic overlays)
 *  - rendererStyle → the inner SVG container div only (scale-to-fit after rotation)
 *
 * Geographic overlays (CardinalsOverlay, SunPathOverlay) rotate together with the
 * wrapper and do NOT need scaling — they use `preserveAspectRatio="xMidYMid meet"`
 * which keeps them inscribed in the wrapper regardless of orientation.
 *
 * @param {object}                          params
 * @param {boolean}                         params.rotatedToNorth  — when true, apply the rotation+scale
 * @param {number|null}                     params.azimuth         — product.azimuth (CCW from +X, CAD convention)
 * @param {{ width: number, height: number }|null} params.svgDimensions  — SVG viewBox dimensions
 * @param {React.RefObject<HTMLElement>}    params.wrapperRef      — ref attached to the rotating wrapper div;
 *   its content-box dimensions are observed via ResizeObserver so northScale stays correct on resize.
 *
 * @returns {{ wrapperStyle: CSSProperties, rendererStyle: CSSProperties }}
 */

import { useEffect, useMemo, useState } from 'react'

import { getRotateToNorthAngleDeg } from '../../../shared/utils/geometryUtils.js'

const TRANSITION = 'transform 0.4s ease'

// ── Pure helper (exported for tests) ─────────────────────────────────────────

/**
 * Computes the CSS rotation angle and scale that rotate the plan to north and
 * keep it within its container.
 *
 * @param {object}                          params
 * @param {boolean}                         params.rotatedToNorth
 * @param {number|null}                     params.azimuth
 * @param {{ width: number, height: number }|null} params.svgDimensions
 * @param {{ width: number, height: number }}      params.containerSize
 *
 * @returns {{ rotationDeg: number, northScale: number }}
 */
export function computeNorthTransform({ rotatedToNorth, azimuth, svgDimensions, containerSize }) {
  const rotationDeg = getRotateToNorthAngleDeg(azimuth ?? 90)

  if (!rotatedToNorth || rotationDeg === 0) {
    return { rotationDeg, northScale: 1 }
  }

  const { width: svgW, height: svgH } = svgDimensions ?? {}
  const { width: containerW, height: containerH } = containerSize

  if (!svgW || !svgH || !containerW || !containerH) {
    return { rotationDeg, northScale: 1 }
  }

  // Step 1: scale SVG to container with aspect-ratio preserved → rendered size.
  // (mirrors what the browser does for svg width/height 100% inside a flex/block container)
  const svgRatio = svgW / svgH
  const containerRatio = containerW / containerH
  const renderedW = svgRatio > containerRatio ? containerW : containerH * svgRatio
  const renderedH = svgRatio > containerRatio ? containerW / svgRatio : containerH

  // Step 2: rotate the 4 corners of the rendered SVG box (centred at origin).
  const hw = renderedW / 2
  const hh = renderedH / 2
  const corners = [[-hw, hh], [hw, hh], [hw, -hh], [-hw, -hh]]
  const rad = (rotationDeg * Math.PI) / 180
  const sin = Math.sin(rad)
  const cos = Math.cos(rad)

  let farthestX = 0
  let farthestY = 0
  for (const [x, y] of corners) {
    farthestX = Math.max(farthestX, Math.abs(cos * x - sin * y))
    farthestY = Math.max(farthestY, Math.abs(sin * x + cos * y))
  }

  // Step 3: the scale that makes the rotated box just fit the container.
  const northScale = Math.min(
    containerW / (2 * farthestX),
    containerH / (2 * farthestY),
  )

  return { rotationDeg, northScale }
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function usePlanNorthRotation({ rotatedToNorth, azimuth, svgDimensions, wrapperRef }) {

  // ── Container size (observed via ResizeObserver) ───────────────────────────
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })

  useEffect(() => {
    const el = wrapperRef?.current
    if (!el) return
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect
      setContainerSize({ width, height })
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [wrapperRef])

  // ── Compute rotation angle and scale ──────────────────────────────────────
  const { rotationDeg, northScale } = useMemo(
    () => computeNorthTransform({ rotatedToNorth, azimuth, svgDimensions, containerSize }),
    [rotatedToNorth, azimuth, svgDimensions, containerSize],
  )

  // ── Style objects ─────────────────────────────────────────────────────────
  // Always include the transition so animation fires in both directions.

  const wrapperStyle = {
    transform: rotatedToNorth ? `rotate(${rotationDeg}deg)` : 'rotate(0deg)',
    transformOrigin: 'center center',
    transition: TRANSITION,
  }

  const rendererStyle = {
    transform: rotatedToNorth ? `scale(${northScale})` : 'scale(1)',
    transformOrigin: 'center center',
    transition: TRANSITION,
  }

  return { wrapperStyle, rendererStyle, activeRotationDeg: rotatedToNorth ? rotationDeg : 0 }
}
